import type { ScrollViewProps } from "@-/com.npmjs.react-native";
import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import type Animated from "@-/com.npmjs.react-native-reanimated";
import {
  cancelAnimation,
  runOnJS,
  scrollTo,
  useAnimatedProps,
  useAnimatedScrollHandler,
  useDerivedValue,
  useSharedValue,
  withSpring,
  type AnimatedRef,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useMemo, useRef } from "react";
import { resistTopSheetOverdrag, topSheetSnapDetent } from "#top-sheet-physics";

const DISMISS_SPRING = { damping: 28, mass: 0.8, overshootClamping: true, stiffness: 260 } as const;
/** How far a pull on the trigger travels before it takes the touch from the strip's own taps. */
const TRIGGER_ACTIVATION_DISTANCE = 12;
/** Where the sheet rests, or last headed: `animatedTarget` values. A drag holds it at -1. */
const COLLAPSED = 0;
const OPEN = 1;
const EXPANDED = 2;
const COMPACT = 3;
/** No content drag holds the scroller in place. */
const NO_SCROLL_LOCK = -1;

/**
 * A `translate` top sheet that rests collapsed behind its anchor, with only its lowest
 * `peekHeight` left showing (nothing at all by default), open at `height`, or expanded to
 * `expandedHeight`. Dragging its handle or header moves it either way. A swipe on its content
 * scrolls the content first and moves the sheet once the content cannot scroll any further, up
 * to collapse it or down to expand it, partway through the same swipe if need be; the content
 * then holds still until the sheet is released. Past the expanded height (or the open height,
 * when it cannot expand), a pull only stretches the sheet against growing resistance. A downward
 * pull on `trigger` (attach it to the strip the sheet hangs from) brings it back, and further
 * down. Releases rest at the position nearest the sheet's projected travel.
 *
 * `offset` is the sheet's position: 0 open, `peekHeight - height` collapsed, `expandedHeight -
 * height` expanded. Below 0 the sheet slides up behind its anchor (`value`, its translateY);
 * above 0 it grows taller (`sheetHeight`), and past the expanded height it slides down. Pressing
 * the handle or header closes an expanded sheet, and it opens again at `height`.
 */
export function useTopSheetDismissMotion({
  compactHeight = 0,
  enabled = true,
  expandedHeight,
  height,
  isOpen,
  onDismissed,
  onOpenChange,
  peekHeight = 0,
  scrollOffset,
  scrollRange,
  scrollRef,
}: {
  /** An additional stop that keeps a summary and the handle visible before full dismissal. */
  compactHeight?: number;
  /** False while there is no sheet to reveal: `trigger` then leaves the strip's touches alone. */
  enabled?: boolean;
  /** How tall a drag can make the open sheet; `height` (no expansion) by default. */
  expandedHeight?: number;
  /** The open sheet's full height, handle included. A change while open eases to the new height. */
  height: number;
  isOpen: boolean;
  /** The sheet finished collapsing, so the content out of sight can reset unseen. */
  onDismissed?: () => void;
  onOpenChange: (isOpen: boolean) => void;
  /** How much of the collapsed sheet stays below its anchor: its header and handle, say. */
  peekHeight?: number;
  /**
   * The content's scroll offset, kept up to date by `scrollHandler` (pass it and
   * `scrollAnimatedProps` to the scroller); omit the scroll values when the content never scrolls.
   */
  scrollOffset?: SharedValue<number>;
  /** The furthest the open sheet's content can scroll (content height minus its viewport). */
  scrollRange?: SharedValue<number>;
  /** The content's scroller, held in place while a content drag moves the sheet. */
  scrollRef?: AnimatedRef<Animated.ScrollView>;
}) {
  const callbacks = useLatest({ onDismissed, onOpenChange });
  const reportDismissed = useMemo(() => () => callbacks.current.onDismissed?.(), [callbacks]);
  const expansionLimit = Math.max(0, (expandedHeight ?? height) - height);
  const openHeight = useSharedValue(height);
  const maximumExpansion = useSharedValue(expansionLimit);
  const peek = useSharedValue(peekHeight);
  const compact = useSharedValue(compactHeight);
  const offset = useSharedValue(isOpen ? 0 : Math.min(0, peekHeight - height));
  const animatedTarget = useSharedValue(isOpen ? OPEN : COLLAPSED);
  const dragActive = useSharedValue(false);
  const dragStart = useSharedValue(0);
  const dragOrigin = useSharedValue(0);
  const scrollLock = useSharedValue(NO_SCROLL_LOCK);
  const touchStart = useSharedValue({ x: 0, y: 0 });
  const expansion = useDerivedValue(() =>
    Math.min(Math.max(0, offset.get()), maximumExpansion.get()),
  );
  const value = useDerivedValue(() => offset.get() - expansion.get());
  const sheetHeight = useDerivedValue(() => openHeight.get() + expansion.get());
  const visibleHeight = useDerivedValue(() => Math.max(0, openHeight.get() + offset.get()));
  const progress = useDerivedValue(
    () =>
      (Math.min(visibleHeight.get(), openHeight.get()) - peek.get()) /
      Math.max(1, openHeight.get() - peek.get()),
  );

  const renderedHeight = useRef(height);
  const renderedPeekHeight = useRef(peekHeight);
  const renderedCompactHeight = useRef(compactHeight);
  const renderedExpansionLimit = useRef(expansionLimit);
  useEffect(() => {
    peek.set(peekHeight);
    compact.set(compactHeight);
    maximumExpansion.set(expansionLimit);
    const collapsed = Math.min(0, peekHeight - height);
    if (
      renderedHeight.current !== height ||
      renderedPeekHeight.current !== peekHeight ||
      renderedCompactHeight.current !== compactHeight ||
      renderedExpansionLimit.current !== expansionLimit
    ) {
      const isFirstMeasurement = renderedHeight.current === 0;
      const isExpanded = animatedTarget.get() === EXPANDED;
      renderedHeight.current = height;
      renderedPeekHeight.current = peekHeight;
      renderedCompactHeight.current = compactHeight;
      renderedExpansionLimit.current = expansionLimit;
      // An open sheet eases to its new height (its first measurement simply appears); a collapsed
      // or expanded one moves straight to its new resting place, keeping the same peek in sight or
      // reaching as far down as before.
      openHeight.set(
        isOpen && !isExpanded && !isFirstMeasurement ? withSpring(height, DISMISS_SPRING) : height,
      );
      if (isOpen && !dragActive.get() && animatedTarget.get() === COMPACT) {
        openHeight.set(height);
        offset.set(Math.max(collapsed, Math.min(0, compactHeight - height)));
        return;
      }
      if (!dragActive.get() && (!isOpen || isExpanded)) {
        cancelAnimation(offset);
        animatedTarget.set(!isOpen ? COLLAPSED : expansionLimit > 0 ? EXPANDED : OPEN);
        offset.set(isOpen ? expansionLimit : collapsed);
        return;
      }
    }
    if (dragActive.get()) {
      return;
    }
    // React observes a release after its UI-thread spring started; keep that spring's velocity.
    // An expanded sheet is open too.
    if (isOpen ? animatedTarget.get() !== COLLAPSED : animatedTarget.get() === COLLAPSED) {
      return;
    }
    animatedTarget.set(isOpen ? OPEN : COLLAPSED);
    offset.set(
      withSpring(isOpen ? 0 : collapsed, DISMISS_SPRING, (finished) => {
        "worklet";
        if (finished === true && !isOpen) {
          runOnJS(reportDismissed)();
        }
      }),
    );
  }, [
    animatedTarget,
    compact,
    compactHeight,
    dragActive,
    expansionLimit,
    height,
    isOpen,
    maximumExpansion,
    offset,
    openHeight,
    peek,
    peekHeight,
    reportDismissed,
  ]);

  // A content drag that moves the sheet holds the content where the drag found it. The scroller
  // still receives the touch: disabling it cancels its own drag (and so any fling on release), and
  // until that lands each scroll event puts the content back.
  const scrollAnimatedProps = useAnimatedProps<ScrollViewProps>(() => ({
    scrollEnabled: scrollLock.get() === NO_SCROLL_LOCK,
  }));
  const scrollHandler = useAnimatedScrollHandler((event) => {
    const lock = scrollLock.get();
    if (lock !== NO_SCROLL_LOCK && scrollRef !== undefined) {
      if (Math.abs(event.contentOffset.y - lock) > 0.5) {
        scrollTo(scrollRef, 0, lock, false);
      }
      return;
    }
    scrollOffset?.set(event.contentOffset.y);
  });

  const gestures = useMemo(() => {
    const reportOpen = (next: boolean) => callbacks.current.onOpenChange(next);
    const begin = (origin: number) => {
      "worklet";
      cancelAnimation(offset);
      dragActive.set(true);
      animatedTarget.set(-1);
      dragStart.set(offset.get());
      dragOrigin.set(origin);
    };
    const follow = (position: number) => {
      "worklet";
      const raw = dragStart.get() + position - dragOrigin.get();
      offset.set(
        resistTopSheetOverdrag(
          raw,
          Math.min(0, peek.get() - openHeight.get()),
          maximumExpansion.get(),
        ),
      );
    };
    const settle = (velocity: number) => {
      "worklet";
      scrollLock.set(NO_SCROLL_LOCK);
      if (!dragActive.get()) {
        return;
      }
      dragActive.set(false);
      const collapsed = Math.min(0, peek.get() - openHeight.get());
      const expanded = maximumExpansion.get();
      const compactOffset = Math.max(collapsed, Math.min(0, compact.get() - openHeight.get()));
      const target = topSheetSnapDetent(offset.get(), velocity, [
        collapsed,
        compactOffset,
        0,
        expanded,
      ]);
      const open = target !== collapsed;
      animatedTarget.set(
        !open ? COLLAPSED : target === 0 ? OPEN : target === compactOffset ? COMPACT : EXPANDED,
      );
      offset.set(
        withSpring(target, { ...DISMISS_SPRING, velocity }, (finished) => {
          "worklet";
          if (finished === true && !open) {
            runOnJS(reportDismissed)();
          }
        }),
      );
      runOnJS(reportOpen)(open);
    };
    // A drag that starts on the handle (or the header) stays with the sheet in every
    // direction: it claims sideways travel before an enclosing pager can, so a diagonal pull never
    // turns the page. Each detector needs its own gesture instance.
    const createDragGesture = () =>
      Gesture.Pan()
        .activeOffsetX([-4, 4])
        .activeOffsetY([-4, 4])
        .onStart(({ translationY }) => {
          "worklet";
          begin(translationY);
        })
        .onUpdate(({ translationY }) => {
          "worklet";
          follow(translationY);
        })
        .onEnd(({ velocityY }) => {
          "worklet";
          settle(velocityY);
        })
        .onFinalize(() => {
          "worklet";
          settle(0);
        });
    // The content keeps its own taps and scrolling. A vertical swipe takes the sheet only once the
    // content has reached its end in that direction, even partway through the same swipe.
    const content = Gesture.Pan()
      .manualActivation(true)
      .onTouchesDown((event) => {
        "worklet";
        touchStart.set({
          x: event.allTouches[0]?.absoluteX ?? 0,
          y: event.allTouches[0]?.absoluteY ?? 0,
        });
      })
      .onTouchesMove((event, manager) => {
        "worklet";
        if (dragActive.get()) {
          return;
        }
        const touch = event.allTouches[0];
        if (touch === undefined) {
          manager.fail();
          return;
        }
        const deltaX = Math.abs(touch.absoluteX - touchStart.get().x);
        const deltaY = touch.absoluteY - touchStart.get().y;
        if (deltaX > 20 && deltaX > Math.abs(deltaY)) {
          manager.fail();
          return;
        }
        const contentOffset = scrollOffset?.get() ?? 0;
        // An expanded sheet shows more of its content, so less of it is left to scroll.
        const isAtEnd = contentOffset >= (scrollRange?.get() ?? 0) - expansion.get() - 1;
        const isAtTop = contentOffset <= 1;
        if ((isAtEnd && deltaY < -4) || (isAtTop && deltaY > 4)) {
          manager.activate();
        }
      })
      .onStart(({ absoluteY }) => {
        "worklet";
        scrollLock.set(Math.max(0, scrollOffset?.get() ?? 0));
        begin(absoluteY);
      })
      .onUpdate(({ absoluteY }) => {
        "worklet";
        follow(absoluteY);
      })
      .onEnd(({ velocityY }) => {
        "worklet";
        settle(velocityY);
      })
      .onFinalize(() => {
        "worklet";
        settle(0);
      });
    // A downward pull on the strip above follows the finger out from behind the anchor. Sideways
    // travel fails it first, so the strip still scrolls and its taps still land.
    const trigger = Gesture.Pan()
      .enabled(enabled)
      .activeOffsetY(TRIGGER_ACTIVATION_DISTANCE)
      .failOffsetX([-20, 20])
      .onStart(({ translationY }) => {
        "worklet";
        begin(translationY);
      })
      .onUpdate(({ translationY }) => {
        "worklet";
        follow(translationY);
      })
      .onEnd(({ velocityY }) => {
        "worklet";
        settle(velocityY);
      })
      .onFinalize(() => {
        "worklet";
        settle(0);
      });
    return {
      content: Gesture.Simultaneous(content, Gesture.Native()),
      handle: createDragGesture(),
      header: createDragGesture(),
      trigger,
    };
  }, [
    animatedTarget,
    callbacks,
    compact,
    dragActive,
    dragOrigin,
    dragStart,
    enabled,
    expansion,
    maximumExpansion,
    offset,
    openHeight,
    peek,
    reportDismissed,
    scrollLock,
    scrollOffset,
    scrollRange,
    touchStart,
  ]);

  return {
    ...gestures,
    contentDraggable: true,
    openHeight,
    progress,
    scrollAnimatedProps,
    scrollHandler,
    sheetHeight,
    value,
    visibleHeight,
    /** Reopen at the full resting height, including from the compact stop. */
    open: () => {
      animatedTarget.set(OPEN);
      offset.set(withSpring(0, DISMISS_SPRING));
      callbacks.current.onOpenChange(true);
    },
  };
}
